import { ref, watch, type Ref } from 'vue';
import { useEditUser, type CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkAssociationDeletePermission } from '@ilteducation/customer-mgmt-permissions';
import { NO_ORGANIZATION_SELECTED_UID, useCurrentOrganization } from './useCurrentOrganization';
import { useLocale } from './useLocale';
import { usePermissions } from './usePermissions';
import useToast from './useToast';
import { useAppEvents } from '../events/appEvents';

export const useCanDeleteUserAssociation = () => {
  const { organization } = useCurrentOrganization();
  const { permissionContext, reporterBoolean, createTargetAssociation, getCallerAssociation } = usePermissions();

  return async (user: CustomerMgmtTypes.GetUserResponse): Promise<boolean> => {
    const currentAssociation = user.associations.find((assoc) => assoc.schoolUnit.uid === organization.value.uid);

    if (!currentAssociation) {
      return false;
    }

    return checkAssociationDeletePermission(
      permissionContext.value,
      createTargetAssociation(user, currentAssociation),
      getCallerAssociation,
      reporterBoolean,
    );
  };
};

/**
 * Deleting a user in the current organization means removing their association with it.
 * Shared by the entry points that offer the action: the edit user modal and the user side panel.
 */
export const useDeleteUserAssociation = (user: Ref<CustomerMgmtTypes.GetUserResponse | null | undefined>) => {
  const { t } = useLocale();
  const { toast } = useToast();
  const { emitAppEvent } = useAppEvents();
  const { organization } = useCurrentOrganization();
  const { deleteAssociation } = useEditUser();
  const canDeleteUserAssociation = useCanDeleteUserAssociation();

  const isUserDeletable = ref<boolean>(false);
  watch(
    () => [user.value, organization.value],
    async () => {
      if (!(user.value && organization.value)) {
        isUserDeletable.value = false;
        return;
      }

      isUserDeletable.value = await canDeleteUserAssociation(user.value);
    },
    { immediate: true },
  );

  /**
   * The backend deactivates the user on whichever removal finds no other associations left, and decides
   * that per call — so the final removal has to land on its own, once the others are done.
   */
  const removeAssociations = async (userUid: string, organizationUids: string[]): Promise<boolean> => {
    const lastUid = organizationUids[organizationUids.length - 1];
    const results = await Promise.all(
      organizationUids.slice(0, -1).map((organizationUid) => deleteAssociation(userUid, organizationUid)),
    );

    return [...results, await deleteAssociation(userUid, lastUid)].every(Boolean);
  };

  const isDeleting = ref<boolean>(false);

  const deleteUser = async (organizationUids?: string[]): Promise<boolean> => {
    const userUid = user.value?.uid;
    const uidsToRemove = (organizationUids?.length ? organizationUids : [organization.value.uid]).filter(
      (uid) => uid !== NO_ORGANIZATION_SELECTED_UID,
    );

    if (!userUid || uidsToRemove.length === 0) {
      return false;
    }

    isDeleting.value = true;
    let isDeleted = false;
    try {
      isDeleted = await removeAssociations(userUid, uidsToRemove);
    } finally {
      isDeleting.value = false;
    }

    emitAppEvent({ type: 'association:updated', userUids: [userUid] });

    if (isDeleted) {
      toast(t('lcp.user_deleted_successfully'), 'success');
      return true;
    }

    toast(t('lcp.failed_to_delete_user'), 'error');
    return false;
  };

  return { isUserDeletable, isDeleting, deleteUser };
};
